import React, { useEffect, useState } from 'react';
import mermaid from 'mermaid';
import RenderAnimation from './RenderMermaidAnimation';
import { iconMapping } from './iconMapping';

interface MermaidRendererProps {
  mermaidSyntax: string;
  svgContainerRef: React.RefObject<HTMLDivElement | null>;
  onRender?: () => void;
  onElementClick?: (element: SVGElement) => void;
  selectedAnimation: string | null;
  animationSpeed: number;
}

const replaceNodeWithIcon = (syntax: string): string => {
  let modifiedSyntax = syntax;

  for (const [service, iconPath] of Object.entries(iconMapping)) {
    const regex = new RegExp(`\\b${service}\\b`, "g");
    modifiedSyntax = modifiedSyntax.replace(
      regex,
      `<img src="${iconPath}" alt="${service}" width="50" height="50" style="vertical-align: middle;"> ${service}`
    );
  }

  return modifiedSyntax;
};

let isMermaidConfigured = false;

if (!isMermaidConfigured) {
  mermaid.initialize({
    startOnLoad: false,
    securityLevel: 'loose',
    maxTextSize: 200000,
    theme: 'neutral',
    flowchart: {
      htmlLabels: true,
      nodeSpacing: 40,
      rankSpacing: 60,
      useMaxWidth: true,
    },
    sequence: {
      useMaxWidth: true,
    },
  });

  isMermaidConfigured = true;
}

const MermaidRenderer: React.FC<MermaidRendererProps> = ({
  mermaidSyntax,
  svgContainerRef,
  onRender,
  onElementClick,
  selectedAnimation,
  animationSpeed,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [svgElement, setSvgElement] = useState<SVGElement | null>(null);
  const [debouncedSyntax, setDebouncedSyntax] = useState(() => mermaidSyntax);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSyntax(mermaidSyntax);
    }, 250);

    return () => {
      window.clearTimeout(handle);
    };
  }, [mermaidSyntax]);

  useEffect(() => {
    if (!svgContainerRef.current) {
      return undefined;
    }

    const container = svgContainerRef.current;
    let isActive = true;
    const removeListeners: Array<() => void> = [];

    const renderDiagram = async () => {
      const syntax = debouncedSyntax.trim();

      if (!syntax) {
        container.innerHTML = '';
        setSvgElement(null);
        return;
      }

      try {
        container.innerHTML = '';

        const modifiedSyntax = replaceNodeWithIcon(syntax);
        const { svg } = await mermaid.render('generated-mermaid', modifiedSyntax);

        if (!isActive) {
          return;
        }

        container.innerHTML = svg;

        const renderedSvg = container.querySelector('svg');
        if (renderedSvg) {
          renderedSvg.classList.add('mermaid-diagram');
          renderedSvg.setAttribute('preserveAspectRatio', 'xMinYMin meet');
          renderedSvg.style.width = '100%';
          renderedSvg.style.height = 'auto';
          renderedSvg.style.maxWidth = '100%';

          if (!renderedSvg.hasAttribute('viewBox')) {
            const bounds = renderedSvg.getBBox();
            renderedSvg.setAttribute('viewBox', `0 0 ${bounds.width} ${bounds.height}`);
          }

          renderedSvg.style.backgroundColor = '#f0f0f0';
          setSvgElement(renderedSvg);

          const interactableElements = renderedSvg.querySelectorAll('.node, .edgePath');
          interactableElements.forEach((element) => {
            const handler = () => onElementClick?.(element as SVGElement);
            element.addEventListener('click', handler);
            removeListeners.push(() => element.removeEventListener('click', handler));
          });
        } else {
          setSvgElement(null);
        }

        setError(null);
        onRender?.();
      } catch (renderError) {
        console.error(renderError);
        if (!isActive) {
          return;
        }
        container.innerHTML = '';
        setSvgElement(null);
        setError('Error rendering Mermaid diagram');
      }
    };

    renderDiagram();

    return () => {
      isActive = false;
      removeListeners.forEach((remove) => remove());
    };
  }, [debouncedSyntax, svgContainerRef, onRender, onElementClick]);

  return (
    <div className="mermaid-renderer">
      {error && <div className="alert alert-danger">{error}</div>}
      {svgElement && (
        <RenderAnimation
          svgElement={svgElement}
          selectedElement={null}
          selectedAnimation={selectedAnimation}
          animationSpeed={animationSpeed}
          circleColor="blue"
          circleRadius={5}
          strokeColor="#000"
          shape="circle"
          uploadedImage={undefined}
          imageWidth={50}
          imageHeight={50}
        />
      )}
    </div>
  );
};

export default MermaidRenderer;
